import type { QueryObserverResult, UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { assignUsersToGroup, getOrganizationUsers, getPermissionGroups, removeUsersFromGroup } from './rbac-client';
import type {
  BulkAssignPayload,
  BulkOperationResult,
  BulkRemovePayload,
  OrganizationUser,
  PermissionGroup,
} from './rbac-client.type';

/**
 * Hook to fetch permission groups for an organization
 *
 * @param {string | null} organizationId The organization ID
 * @returns Query result with permission groups data
 */
function usePermissionGroups(organizationId: string | null): {
  data: PermissionGroup[] | undefined;
  query: QueryObserverResult<{ results: PermissionGroup[] }>;
} {
  const query = useQuery({
    queryKey: ['permission-groups', organizationId],
    queryFn: () => getPermissionGroups(organizationId!),
    enabled: !!organizationId,
  });

  return { data: query.data?.results, query };
}

/**
 * Hook to fetch all users in an organization
 *
 * @param {string | null} organizationId The organization ID
 * @returns Query result with organization users data
 */
function useOrganizationUsers(organizationId: string | null): {
  data: OrganizationUser[] | undefined;
  query: QueryObserverResult<{ results: OrganizationUser[] }>;
} {
  const query = useQuery({
    queryKey: ['organization-users', organizationId],
    queryFn: () => getOrganizationUsers(organizationId!),
    enabled: !!organizationId,
  });

  return { data: query.data?.results, query };
}

/**
 * Hook to assign users to a permission group
 *
 * @param {Object} options Mutation options
 * @param {Function} options.onSuccess Callback on success
 * @returns Mutation result
 */
function useAssignUsersToGroup({
  onSuccess,
}: {
  onSuccess?: () => void;
} = {}): UseMutationResult<BulkOperationResult, Error, BulkAssignPayload> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['assignUsersToGroup'],
    mutationFn: (payload: BulkAssignPayload) => assignUsersToGroup(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permission-groups'] });
      queryClient.invalidateQueries({ queryKey: ['group-members'] });
      onSuccess?.();
    },
  });
}

/**
 * Hook to remove users from a permission group
 *
 * @param {Object} options Mutation options
 * @param {Function} options.onSuccess Callback on success
 * @returns Mutation result
 */
function useRemoveUsersFromGroup({
  onSuccess,
}: {
  onSuccess?: () => void;
} = {}): UseMutationResult<BulkOperationResult, Error, BulkRemovePayload> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['removeUsersFromGroup'],
    mutationFn: (payload: BulkRemovePayload) => removeUsersFromGroup(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permission-groups'] });
      queryClient.invalidateQueries({ queryKey: ['group-members'] });
      onSuccess?.();
    },
  });
}

export { useAssignUsersToGroup, useOrganizationUsers, usePermissionGroups, useRemoveUsersFromGroup };
